import React, { useState, useRef } from 'react';
import { XMarkIcon, TrashIcon, PencilIcon, FolderIcon } from '@heroicons/react/24/outline';
import { useNotes } from '../context/NotesContext';
import { useError } from '../context/ErrorContext';
import { useClickOutside } from '../hooks/useClickOutside';
import ConfirmModal from './ConfirmModal';

const FolderManageModal = ({ onClose }) => {
  const { folders, addFolder, deleteFolder, updateFolder } = useNotes();
  const { showError } = useError();
  const [newFolderName, setNewFolderName] = useState('');
  const [editingFolder, setEditingFolder] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const modalRef = useClickOutside(() => onClose());
  const [folderToDelete, setFolderToDelete] = useState(null);

  // Filter out Default folder and ensure All Notes is first but not editable
  const managedFolders = folders.filter(folder => folder !== 'All Notes');

  const handleCreateFolder = (e) => {
    e.preventDefault();
    if (!newFolderName.trim()) {
      showError('Please enter a folder name');
      return;
    }

    if (folders.includes(newFolderName.trim())) {
      showError('Folder already exists');
      return;
    }

    if (addFolder(newFolderName.trim())) {
      setNewFolderName('');
    }
  };

  const handleRename = (folder, newName) => {
    if (newName.trim() === '') {
      showError('Folder name cannot be empty');
      return;
    }
    if (folders.includes(newName) && newName !== folder) {
      showError('Folder name already exists');
      return;
    }
    updateFolder(folder, newName);
    setEditingFolder(null);
  };

  const handleDeleteConfirm = async () => {
    if (await deleteFolder(folderToDelete)) {
      setFolderToDelete(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div 
        ref={modalRef}
        className="bg-dark-card rounded-lg w-full max-w-md p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">Manage Folders</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* New Folder Input */}
        <form onSubmit={handleCreateFolder} className="mb-6">
          <input
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder="New folder name"
            className="w-full bg-dark-bg text-white px-4 py-2 rounded-lg mb-3"
          />
          <button
            type="submit"
            className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition-colors"
          >
            Create Folder
          </button>
        </form>

        {/* Folder List */}
        <div className="space-y-2">
          {/* All Notes folder - not editable */}
          <div className="p-3 rounded-lg bg-dark-bg text-gray-400">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <FolderIcon className="w-4 h-4 mr-2" />
                <span>All Notes</span>
              </div>
            </div>
          </div>

          {/* User created folders */}
          {managedFolders.map(folder => (
            <div
              key={folder}
              className="p-3 rounded-lg bg-dark-bg text-gray-400 hover:bg-dark-hover/50"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <FolderIcon className="w-4 h-4 mr-2" />
                  {editingFolder === folder ? (
                    <input
                      type="text"
                      value={editingValue}
                      onChange={(e) => setEditingValue(e.target.value)}
                      className="bg-dark-bg text-white px-2 py-1 rounded"
                      autoFocus
                      onBlur={() => handleRename(folder, editingValue)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleRename(folder, editingValue);
                        } else if (e.key === 'Escape') {
                          setEditingFolder(null);
                          setEditingValue('');
                        }
                      }}
                    />
                  ) : (
                    <span>{folder}</span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      setEditingFolder(folder);
                      setEditingValue(folder);
                    }}
                    className="p-1 hover:text-white"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setFolderToDelete(folder)}
                    className="p-1 hover:text-red-500"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <ConfirmModal
        isOpen={folderToDelete !== null}
        onClose={() => setFolderToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Folder"
        message={`Are you sure you want to delete "${folderToDelete}"? The notes inside will be moved to All Notes.`}
      />
    </div>
  );
};

export default FolderManageModal; 